/**
 * ONE way to put a JSON-LD object inside a <script> tag (FRIDGE-517).
 *
 * The hole this closes
 * ====================
 * `JSON.stringify` escapes what JSON needs — quotes, backslashes, control
 * characters — and nothing else. It does NOT escape `<`, because `<` is a
 * perfectly ordinary JSON character. But the output does not land in a JSON
 * file; it lands between `<script type="application/ld+json">` and
 * `</script>`, and an HTML parser reading that raw-text element stops at the
 * FIRST `</script`, wherever it appears. So one stored product title, FAQ
 * answer, recall hazard or press body containing the literal text
 * `</script>` would close the tag early and hand the rest of the JSON to the
 * HTML parser as markup.
 *
 * Every schema node on this site is built from database text — product names,
 * descriptions, FAQ answers, breadcrumb labels, video titles — so the input is
 * not under our control, only the encoding is.
 *
 * Verified 08/24/2026: ZERO occurrences of `</script`, `<script` or `<!--` in
 * live schema-fed data. This is HARDENING, not a live hole. It is worth doing
 * anyway because the cost is one function and the failure mode is markup
 * injection on ~90K pages.
 *
 * Why `<` and not HTML entities
 * ==================================
 * `<` is a standard JSON string escape, so the emitted text is still
 * valid JSON and every consumer (Google, an AI assistant, `JSON.parse`)
 * decodes it back to `<` — the DATA is unchanged. An HTML entity (`&lt;`)
 * would NOT be decoded, because a `<script>` element is raw text: entities are
 * not processed there, so `&lt;` would corrupt the value instead of escaping
 * it.
 *
 * Only `<` needs escaping. `>` and `&` cannot start a tag or a comment, and
 * inside raw text they carry no special meaning; `</script`, `<script` and
 * `<!--` — the three sequences an HTML parser reacts to — all begin with `<`.
 *
 * Usage — two sanctioned shapes, and no third:
 *
 *   a plain <script type="application/ld+json"> whose `__html` is
 *   `safeJsonLd(schema)`, or a next/script <Script type="application/ld+json">
 *   whose single child expression is `safeJsonLd(schema)`.
 *
 * (The prop name is spelled out in prose rather than as code here on purpose:
 * tests/article-html-sanitizer-and-csp.test.ts counts raw-HTML injection SITES
 * by matching the prop, and a documentation example would read as a new one.)
 *
 * Pinned by tests/json-ld-escaping.test.ts, which fails both on any
 * `application/ld+json` script that still stringifies raw AND on any emission
 * shape it does not recognise.
 */

/**
 * Serialize a JSON-LD object for embedding in a `<script>` element.
 *
 * Returns `'null'` when the value is not serializable (`undefined`, a bare
 * function, a symbol). That is deliberate: raw `JSON.stringify(undefined)`
 * returns `undefined`, which React coerces to the literal text `undefined` in
 * the script body — text that no JSON parser accepts. `null` at least parses.
 * A schema block should be gated by its caller rather than reaching here empty.
 */
export function safeJsonLd(value: unknown): string {
  const json = JSON.stringify(value)
  if (typeof json !== 'string') return 'null'
  return json.replace(/</g, '\\u003c')
}
